import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AgentAvatarComponent } from '../agent-avatar/agent-avatar.component';
import {
  AgentProfilePictureService,
  PROFILE_PICTURE_MAX_BYTES,
  PROFILE_PICTURE_TYPES
} from '../../../services/agent-profile-picture.service';

/** Upload, replace or remove an agent's profile picture, with inline feedback (no pop-ups). */
@Component({
  selector: 'app-agent-profile-picture-editor',
  standalone: true,
  imports: [CommonModule, AgentAvatarComponent],
  template: `
    <div class="flex items-center gap-4 flex-wrap">
      <div role="img" [attr.aria-label]="(url ? 'Profile photo of ' : 'No profile photo for ') + agentName">
        <app-agent-avatar [url]="url" [name]="agentName" sizeClass="w-20 h-20 text-xl" shapeClass="rounded-2xl"></app-agent-avatar>
      </div>
      <div class="flex flex-col gap-2">
        <div class="flex gap-2 flex-wrap">
          <label class="cursor-pointer min-h-[44px] px-4 inline-flex items-center gap-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-medium focus-within:ring-2 focus-within:ring-green-400 focus-within:ring-offset-2"
            [class.opacity-50]="busy" [class.pointer-events-none]="busy">
            <i class="fas fa-camera" aria-hidden="true"></i> {{ url ? 'Replace photo' : 'Upload photo' }}
            <input type="file" class="sr-only" [accept]="accept" (change)="onFileSelected($event)" [disabled]="busy" />
          </label>
          <button *ngIf="url && !confirmingRemove" type="button"
            class="min-h-[44px] px-4 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium disabled:opacity-50"
            (click)="confirmingRemove = true" [disabled]="busy">
            <i class="fas fa-trash" aria-hidden="true"></i> Remove
          </button>
          <ng-container *ngIf="confirmingRemove">
            <span class="text-xs text-gray-600 self-center">Remove this photo?</span>
            <button type="button" class="min-h-[44px] px-4 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-50"
              (click)="remove()" [disabled]="busy">Yes, remove</button>
            <button type="button" class="min-h-[44px] px-4 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium disabled:opacity-50"
              (click)="confirmingRemove = false" [disabled]="busy">Keep</button>
          </ng-container>
        </div>
        <span class="text-xs text-gray-500">JPEG, PNG or WebP, up to 5 MB.</span>
        <div role="status" aria-live="polite" class="text-xs">
          <span *ngIf="busy" class="text-gray-600"><i class="fas fa-spinner fa-spin" aria-hidden="true"></i> Saving…</span>
          <span *ngIf="message && !busy && !isError" class="text-green-700">{{ message }}</span>
        </div>
        <div role="alert" class="text-xs text-red-600">
          <span *ngIf="message && !busy && isError">{{ message }}</span>
        </div>
      </div>
    </div>
  `
})
export class AgentProfilePictureEditorComponent {
  @Input({ required: true }) agentId!: string;
  @Input() agentName = '';
  @Input() url: string | null = null;

  readonly accept = PROFILE_PICTURE_TYPES.join(',');
  busy = false;
  confirmingRemove = false;
  message = '';
  isError = false;

  constructor(private pictures: AgentProfilePictureService) {}

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }
    // Some systems report no MIME type (often for .webp); fall back to the file extension.
    const looksLikePicture = file.type
      ? PROFILE_PICTURE_TYPES.includes(file.type)
      : /\.(jpe?g|png|webp)$/i.test(file.name);
    if (!looksLikePicture) {
      this.show('Choose a JPEG, PNG or WebP picture.', true);
      return;
    }
    if (file.size > PROFILE_PICTURE_MAX_BYTES) {
      this.show('The picture must be 5 MB or smaller.', true);
      return;
    }

    this.busy = true;
    this.confirmingRemove = false;
    shrinkForAvatar(file)
      .then(small => this.upload(small))
      .catch(() => {
        this.busy = false;
        this.show('Could not read that picture. Try a different file.', true);
      });
  }

  private upload(file: File): void {
    this.pictures.upload(this.agentId, file).subscribe({
      next: picture => {
        this.busy = false;
        this.url = picture.url;
        this.show('Photo saved.', false);
      },
      error: err => {
        this.busy = false;
        this.show(err?.error?.detail || err?.error?.error || 'Could not save the photo. Try again.', true);
      }
    });
  }

  remove(): void {
    this.busy = true;
    this.pictures.remove(this.agentId).subscribe({
      next: () => {
        this.busy = false;
        this.confirmingRemove = false;
        this.url = null;
        this.show('Photo removed.', false);
      },
      error: () => {
        this.busy = false;
        this.show('Could not remove the photo. Try again.', true);
      }
    });
  }

  private show(text: string, isError: boolean): void {
    this.message = text;
    this.isError = isError;
  }
}

/** Longest side of a stored avatar; plenty for the largest (80px) display at high density. */
const AVATAR_MAX_PX = 512;

/**
 * Shrinks a picture to at most 512px on its longest side and re-encodes it as JPEG.
 * Avatars are shown at 20-80px, so full-size phone photos would waste bandwidth and memory;
 * re-encoding also drops EXIF metadata such as GPS location.
 */
function shrinkForAvatar(file: File): Promise<File> {
  return createImageBitmap(file).then(bitmap => {
    const scale = Math.min(1, AVATAR_MAX_PX / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Canvas is not available');
    }
    // JPEG has no transparency, so fill it white first.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    return new Promise<File>((resolve, reject) =>
      canvas.toBlob(
        blob => blob
          ? resolve(new File([blob], 'avatar.jpg', { type: 'image/jpeg' }))
          : reject(new Error('Could not encode picture')),
        'image/jpeg',
        0.85
      )
    );
  });
}
